import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Star, ArrowLeft, Loader2, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard, FullPageLoader } from "@/components/common";

export default function ReviewSubmit() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [booking, setBooking] = useState(null);
  const [existing, setExisting] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [busy, setBusy] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [pointsPerReview, setPointsPerReview] = useState(0);

  useEffect(() => {
    apiClient.get("/reviews/public-settings").then(({ data }) => {
      setPointsPerReview(Number(data?.points_per_review) || 0);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(`/review/${bookingId}`)}`, { replace: true });
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const [{ data: eligible }, { data: mine }] = await Promise.all([
          apiClient.get("/reviews/eligible-bookings"),
          apiClient.get(`/reviews/booking/${bookingId}`),
        ]);
        if (!mounted) return;
        if (mine) {
          setExisting(mine);
          setRating(mine.rating);
          setTitle(mine.title || "");
          setBody(mine.body || "");
        }
        const match = (eligible || []).find((b) => b.booking_id === bookingId);
        if (match) {
          setBooking(match);
        } else if (!mine) {
          setNotFound(true);
        }
      } catch (e) {
        toast.error(getErrorMessage(e, "Could not load review"));
      } finally {
        if (mounted) setBusy(false);
      }
    })();
    return () => { mounted = false; };
  }, [bookingId, user, loading, navigate]);

  const active = hover || rating;
  const isEditing = !!existing;
  const canEdit = !existing || existing.editable;

  const submit = async () => {
    if (rating < 1) { toast.error("Please pick a star rating"); return; }
    if (body.trim().length < 4) { toast.error("Your review is too short"); return; }
    setSubmitting(true);
    try {
      if (isEditing) {
        const { data } = await apiClient.patch(`/reviews/${existing.id}`, {
          rating, title: title.trim() || null, body: body.trim(),
        });
        setExisting(data);
        toast.success("Review updated");
      } else {
        await apiClient.post("/reviews", {
          booking_id: bookingId, rating, title: title.trim() || null, body: body.trim(),
        });
        toast.success("Thanks — your review is in! Points added.");
      }
      navigate("/my-bookings");
    } catch (err) {
      toast.error(getErrorMessage(err, "Submission failed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || busy) return <FullPageLoader label="Loading review" />;

  return (
    <div>
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-400 hover:text-white mb-4" data-testid="review-back">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <PageHeader
        title={isEditing ? "Edit your review" : "How was your visit?"}
        subtitle={isEditing
          ? (canEdit ? "You can still tweak this one — the edit window is open." : "Edit window has closed, but you can still read it below.")
          : "Share your experience and earn bonus points!"}
      />

      {notFound ? (
        <SectionCard>
          <p className="text-slate-300">We couldn&apos;t find a completed visit to review here.</p>
        </SectionCard>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <SectionCard>
              {booking ? (
                <div className="mb-6 pb-6 border-b border-slate-800">
                  <p className="text-slate-500 text-xs tracking-widest uppercase mb-2">Your visit</p>
                  <p className="font-display text-xl text-white">
                    {booking.station_names.join(", ") || "Session"}
                  </p>
                  <p className="text-slate-400 text-sm mt-1">
                    {format(parseISO(booking.end_time), "PPP · p")}
                  </p>
                </div>
              ) : existing ? (
                <div className="mb-6 pb-6 border-b border-slate-800">
                  <p className="text-slate-500 text-xs tracking-widest uppercase mb-2">Review submitted</p>
                  <p className="text-slate-400 text-sm">
                    {format(parseISO(existing.created_at), "PPP · p")}
                  </p>
                </div>
              ) : null}

              <label className="text-white text-xs font-bold tracking-widest mb-2 block">Your rating</label>
              <div className="flex gap-2 mb-6" data-testid="review-rating">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    type="button"
                    disabled={!canEdit}
                    onMouseEnter={() => canEdit && setHover(s)}
                    onMouseLeave={() => canEdit && setHover(0)}
                    onClick={() => canEdit && setRating(s)}
                    data-testid={`review-star-${s}`}
                    className="transition-transform hover:scale-110 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    <Star
                      className="w-10 h-10"
                      strokeWidth={1.5}
                      style={{
                        color: s <= active ? "#ffb020" : "#334155",
                        fill: s <= active ? "#ffb020" : "transparent",
                      }}
                    />
                  </button>
                ))}
              </div>

              <label className="text-white text-xs font-bold tracking-widest mb-2 block">
                Title <span className="text-slate-500 normal-case tracking-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={!canEdit}
                maxLength={120}
                placeholder="Nail the vibe in one line"
                className="input-cyber mb-5"
                data-testid="review-title-input"
              />

              <label className="text-white text-xs font-bold tracking-widest mb-2 block">Your review</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                disabled={!canEdit}
                maxLength={2000}
                rows={7}
                placeholder="Tell other racers what made your visit awesome (or how we could do better)."
                className="input-cyber resize-none"
                data-testid="review-body-input"
              />
              <p className="text-slate-500 text-xs mt-1 text-right">{body.length}/2000</p>

              {canEdit ? (
                <button onClick={submit} disabled={submitting} className="neon-btn px-8 py-3 mt-6" data-testid="review-submit">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : (isEditing ? "Save changes" : "Submit review")}
                </button>
              ) : (
                <div className="mt-6 flex items-center gap-2 text-slate-400 text-sm">
                  <Lock className="w-4 h-4" /> Edit window has closed — this review is locked.
                </div>
              )}
            </SectionCard>
          </div>

          <div className="lg:col-span-1 space-y-6">
            <SectionCard>
              <div className="flex items-center gap-3 mb-3">
                <Sparkles className="w-5 h-5" style={{ color: "var(--neon-cyan)" }} />
                <h3 className="font-display text-lg font-bold text-white uppercase tracking-wider">Points reward</h3>
              </div>
              <p className="text-slate-400 text-sm">
                {pointsPerReview > 0
                  ? <>Submit a review to earn <span className="font-bold neon-cyan-text">{pointsPerReview.toLocaleString()}</span> bonus points. We read every review!</>
                  : "Submit a review to earn bonus points. We read every review!"}
              </p>
            </SectionCard>
          </div>
        </div>
      )}
    </div>
  );
}
